import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/Home/Hero';
import About from '../components/Home/About';
import BackgroundAudio from '../components/Home/BackgroundAudio';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaCertificate,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaDrum,
  FaYoutube,
  FaInstagram,
  FaFacebook
} from 'react-icons/fa';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Home = () => {

  const handleCallNow = () => {
    window.open('tel:+918275664288', '_blank');
  };

  const handleGetDirections = () => {
    const address = encodeURIComponent('Green Woods 302 Kaspate Vasti, Pune, Maharashtra, India');
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  return (
    <div className="min-h-screen font-sans">
      <SEO 
        title="Taalsetu • Learn Tabla Online • Master Atharva Kagalkar"
        description="Master the art of Tabla with Taalsetu. Authentic online and offline lessons by Maestro Atharva Kagalkar. Traditional Indian classical music education globally."
        keywords="learn tabla, tabla online classes, tabla classes near me, online tabla lessons, Atharva Kagalkar, Taalsetu, learn indian classical music, tabla teacher, best tabla classes in pune, wakad tabla classes, tabla certification, rhythm, classical percussion, beginner tabla lessons, advanced tabla courses"
        url="https://www.taalsetu.com/"
      />
      {/* Hero Section */}
      <Hero />

      {/* About Section */}
      <About />

      {/* Features / Why Choose Section */}
      <motion.section
        id="why-taalsetu"
        className="py-24 bg-white dark:bg-maroon-dark relative overflow-hidden transition-colors duration-500"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUp}
      >
        {/* Subtle gold decorative bg */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute top-10 left-10 w-40 h-40 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-gold rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="section-frame">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">
                Why Choose <span className="text-gold">Taalsetu</span>
              </h2>
              <div className="gold-divider mb-6" />
              <p className="font-sans text-lg text-maroon/60 dark:text-gold-light/60 max-w-3xl mx-auto transition-colors duration-500">
                Experience the perfect blend of traditional tabla teaching with modern online learning technology
              </p>
            </div>

            <motion.div 
              className="grid md:grid-cols-3 gap-8"
              variants={staggerContainer}
            >
              {[
                {
                  icon: <FaDrum className="text-4xl text-gold" />,
                  title: "Traditional Teaching",
                  description: "Learn authentic tabla techniques passed down through generations of masters"
                },
                {
                  icon: <FaUsers className="text-4xl text-gold" />,
                  title: "Personalized Learning",
                  description: "One-on-one attention and customized learning paths for every student"
                },
                {
                  icon: <FaCertificate className="text-4xl text-gold" />,
                  title: "Certified Programs",
                  description: "Structured curriculum with recognized certifications in classical music"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white/60 dark:bg-white/5 rounded-xl p-8 text-center border border-gold/10 hover:border-gold/25 transition-all duration-400 hover:shadow-lg group"
                >
                  <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="font-serif text-xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">{feature.title}</h3>
                  <p className="font-sans text-maroon/55 dark:text-gold-light/55 leading-relaxed transition-colors duration-500">{feature.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Tabla Courses — the only internal links into /courses/*. Without these
          the course pages are orphaned and crawlers never reach them. */}
      <motion.section
        id="courses"
        className="py-24 bg-cream dark:bg-maroon relative overflow-hidden transition-colors duration-500"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUp}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">
              Our Tabla <span className="text-gold">Courses</span>
            </h2>
            <div className="gold-divider mb-6" />
            <p className="font-sans text-lg text-maroon/60 dark:text-gold-light/60 max-w-3xl mx-auto transition-colors duration-500">
              Structured programmes for every stage of your journey — taught online and at our Wakad studio in Pune by Maestro Atharva Kagalkar.
            </p>
          </div>

          <motion.div className="grid md:grid-cols-3 gap-8" variants={staggerContainer}>
            {[
              {
                title: "Beginner Tabla Course",
                to: "/courses/beginner/",
                description: "Start from zero — parts of the tabla, basic bols, Teentaal, your first kaida, and a full performance piece in 12 weeks.",
              },
              {
                title: "Intermediate Tabla Course",
                to: "/courses/intermediate/",
                description: "Expand into Keherwa, Rupak and Jhaptaal, rela and tukda compositions, peshkar, and accompanying vocalists.",
              },
              {
                title: "Advanced Tabla Course",
                to: "/courses/advanced/",
                description: "Gharana styles, extended compositions, improvisation and jugalbandi, rare taals, and solo recital design.",
              },
            ].map((course) => (
              <motion.div key={course.to} variants={fadeInUp} className="h-full">
                <Link
                  to={course.to}
                  className="flex flex-col h-full bg-white/60 dark:bg-white/5 rounded-xl p-8 border border-gold/10 hover:border-gold/30 hover:shadow-lg transition-all duration-400 group"
                >
                  <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    <FaDrum className="text-4xl text-gold" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-maroon dark:text-gold-light mb-4 text-center transition-colors duration-500">
                    {course.title}
                  </h3>
                  <p className="font-sans text-maroon/55 dark:text-gold-light/55 leading-relaxed text-center flex-grow transition-colors duration-500">
                    {course.description}
                  </p>
                  <span className="mt-6 font-sans font-medium text-gold text-center group-hover:underline">
                    View course →
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-12">
            <Link to="/blog/" className="font-sans font-medium text-maroon dark:text-gold-light hover:text-gold transition-colors duration-300">
              Read our tabla learning guides →
            </Link>
          </div>
        </div>
      </motion.section>

      {/* Head Office Address Section */}
      <motion.section
        id="contact"
        className="py-24 bg-maroon text-white relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeInUp}
      >
        {/* Subtle decorative elements */}
        <div className="absolute inset-0 opacity-[0.05]">
          <div className="absolute top-20 left-20 w-60 h-60 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-gold rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-14">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              Visit Our <span className="text-gold">Head Office</span>
            </h2>
            <div className="gold-divider mb-6" />
            <p className="font-sans text-lg text-gold-light/60 max-w-3xl mx-auto">
              Connect with us in person for personalized guidance and support
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Address Information */}
            <motion.div className="space-y-8" variants={fadeInUp}>
              <div className="bg-maroon-dark/50 rounded-xl p-8 border border-gold/10">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-gold/20 p-3 rounded-lg">
                    <FaMapMarkerAlt className="text-gold text-xl" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-gold-light mb-2">Head Office</h3>
                    <p className="font-sans text-gold-light/60 leading-relaxed">
                      Green Woods 302<br />
                      Kaspate Vasti, Wakad<br />
                      Pune, Maharashtra<br />
                      India
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gold/15 p-2 rounded-lg">
                      <FaPhoneAlt className="text-gold text-sm" />
                    </div>
                    <div>
                      <p className="text-sm text-gold-light/40 font-sans">Phone</p>
                      <p className="text-gold-light font-medium font-sans">+91 82756 64288</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="bg-gold/15 p-2 rounded-lg">
                      <FaEnvelope className="text-gold text-sm" />
                    </div>
                    <div>
                      <p className="text-sm text-gold-light/40 font-sans">Email</p>
                      <p className="text-gold-light font-medium font-sans">taalsetu@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 md:col-span-2">
                    <div className="bg-gold/15 p-2 rounded-lg">
                      <FaClock className="text-gold text-sm" />
                    </div>
                    <div>
                      <p className="text-sm text-gold-light/40 font-sans">Office Hours</p>
                      <p className="text-gold-light font-medium font-sans">8am to 10pm</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={handleCallNow}
                  className="btn-gold flex items-center justify-center !rounded-xl"
                >
                  <FaPhoneAlt className="mr-2" />
                  Call Now
                </button>

                <button
                  onClick={handleGetDirections}
                  className="flex items-center justify-center px-6 py-3.5 text-gold-light border border-gold/30 rounded-xl hover:bg-gold/10 transition-all duration-300 font-medium"
                >
                  <FaMapMarkerAlt className="mr-2" />
                  Get Directions
                </button>
              </div>
            </motion.div>

            {/* Map/Visual Element */}
            <motion.div className="relative" variants={fadeInUp}>
              <div className="bg-gradient-to-br from-gold/20 to-gold/10 rounded-2xl p-8 text-center border border-gold/15">
                <div className="bg-maroon-dark/30 rounded-xl p-8 backdrop-blur-sm">
                  <FaMapMarkerAlt className="text-gold text-5xl mx-auto mb-6" />
                  <h3 className="font-serif text-2xl font-bold text-gold-light mb-4">Find Us Here</h3>
                  <p className="font-sans text-gold-light/60 mb-6">
                    Located in the heart of Wakad, Pune — easily accessible by public transport and major highways.
                  </p>

                  <div className="space-y-3 text-left">
                    {[
                      'Near Wakad Metro Station',
                      '5 min from Hinjewadi IT Park',
                      'Ample parking available'
                    ].map((info, i) => (
                      <div key={i} className="flex items-center space-x-3 text-gold-light/60">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full" />
                        <span className="text-sm font-sans">{info}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gold/10 rounded-full blur-lg" />
              <div className="absolute -bottom-4 -left-4 w-14 h-14 bg-gold/15 rounded-full blur-lg" />
            </motion.div>
          </div>


        </div>
      </motion.section>

      {/* Footer */}
      <footer className="bg-maroon-dark py-14 relative overflow-hidden border-t border-gold/10">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-10 right-10 w-40 h-40 bg-gold rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <h3 className="font-serif text-2xl font-bold text-gold mb-4">Taalsetu</h3>
              <p className="font-sans text-gold-light/50 mb-6 max-w-md leading-relaxed">
                Empowering learners worldwide with authentic classical music education.
                Join hundreds of students who have mastered the rhythm of Indian classical music with us.
              </p>
              <div className="font-sans text-gold-light/40 text-sm">
                <p className="font-medium text-gold-light/60 mb-1">Head Office:</p>
                <p>Green Woods 302, Kaspate Vasti</p>
                <p>Wakad, Pune, Maharashtra, India</p>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-serif text-lg font-semibold text-gold mb-4">Quick Links</h4>
              <ul className="space-y-2 font-sans text-gold-light/40">
                <li><Link to="/#about" className="hover:text-gold transition-colors duration-300">About Us</Link></li>
                <li><Link to="/courses/beginner/" className="hover:text-gold transition-colors duration-300">Beginner Tabla Course</Link></li>
                <li><Link to="/courses/intermediate/" className="hover:text-gold transition-colors duration-300">Intermediate Tabla Course</Link></li>
                <li><Link to="/courses/advanced/" className="hover:text-gold transition-colors duration-300">Advanced Tabla Course</Link></li>
                <li><Link to="/blog/" className="hover:text-gold transition-colors duration-300">Blog</Link></li>
                <li><Link to="/gallery/" className="hover:text-gold transition-colors duration-300">Gallery</Link></li>
                <li><Link to="/#contact" className="hover:text-gold transition-colors duration-300">Contact</Link></li>
                <li><Link to="/login" className="hover:text-gold transition-colors duration-300">Login</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-serif text-lg font-semibold text-gold mb-4">Contact</h4>
              <ul className="space-y-3 font-sans text-gold-light/40">
                <li className="flex items-center space-x-2">
                  <FaPhoneAlt className="text-gold text-xs" />
                  <a href="tel:+918275664288" className="hover:text-gold transition-colors duration-300">+91 82756 64288</a>
                </li>
                <li className="flex items-center space-x-2">
                  <FaEnvelope className="text-gold text-xs" />
                  <a href="mailto:taalsetu@gmail.com" className="hover:text-gold transition-colors duration-300">taalsetu@gmail.com</a>
                </li>
                <li className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="text-gold text-xs" />
                  <span>Wakad, Pune</span>
                </li>
              </ul>
              
              {/* Social Links */}
              <div className="flex space-x-4 mt-6">
                <a href="https://youtube.com/@taalsetumusicacademy?si=xKqH1lYomk3qOFR9" target="_blank" rel="noopener noreferrer" className="text-gold-light/40 hover:text-gold transition-colors duration-300 text-2xl">
                  <FaYoutube />
                </a>
                <a href="https://www.instagram.com/taalsetu?igsh=Z21lMnh2ZmFuOXZ2" target="_blank" rel="noopener noreferrer" className="text-gold-light/40 hover:text-gold transition-colors duration-300 text-2xl">
                  <FaInstagram />
                </a>
                <a href="https://www.facebook.com/share/14XWpqudVJV/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-gold-light/40 hover:text-gold transition-colors duration-300 text-2xl">
                  <FaFacebook />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gold/10 mt-10 pt-8 text-center">
            <p className="font-sans text-gold-light/30 text-sm">© 2026 Taalsetu. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Background Audio (Home Page Only) */}
      <BackgroundAudio />
    </div>
  );
};

export default Home;